import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getLeads, updateLeadStage, getViewings, getAdminValuations, getAdminEnquiries, addLeadManual, importLeads, convertLead, updateViewingStatus, addLeadNote, setLeadFollowup, submitViewingFeedback } from "../api";
import { Users, CalendarCheck, Home, Mail, TrendingUp, Plus, Upload, UserPlus, Check, X, MessageSquarePlus, AlarmClock, LayoutGrid, Building2, Wrench, Settings, FileUp, PoundSterling, Sparkles } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent } from "../components/ui/dialog";
import AdminProperties from "../components/admin/AdminProperties";
import AdminUsers from "../components/admin/AdminUsers";
import AdminCompliance from "../components/admin/AdminCompliance";
import AdminMaintenance from "../components/admin/AdminMaintenance";
import AdminPayments from "../components/admin/AdminPayments";
import AdminMatching from "../components/admin/AdminMatching";
import AdminCRM from "../components/admin/AdminCRM";
import AdminViewings from "../components/admin/AdminViewings";
import AdminProgression from "../components/admin/AdminProgression";
import AdminAnalytics from "../components/admin/AdminAnalytics";
import AdminAgency from "../components/admin/AdminAgency";
import AdminTemplates from "../components/admin/AdminTemplates";
import AdminIntegrations from "../components/admin/AdminIntegrations";
import AdminValuations from "../components/admin/AdminValuations";
import AdminEnquiries from "../components/admin/AdminEnquiries";
import AdminCalendar from "../components/admin/AdminCalendar";
import AdminPhotoLibrary from "../components/admin/AdminPhotoLibrary";
import AdminApiDirectory from "../components/admin/AdminApiDirectory";
import AdminFinance from "../components/admin/AdminFinance";
import AdminDataImport from "../components/admin/AdminDataImport";
import AdminAppSync from "../components/admin/AdminAppSync";
import AdminEmailTemplate from "../components/admin/AdminEmailTemplate";
import AdminSavedSearches from "../components/admin/AdminSavedSearches";
import AdminSuppressionList from "../components/admin/AdminSuppressionList";
import AdminBlog from "../components/admin/AdminBlog";
import AdminServiceRequests from "../components/admin/AdminServiceRequests";
import AdminCommandCentre from "../components/admin/intelligence/AdminCommandCentre";
import AdminLandlordFinder from "../components/admin/intelligence/AdminLandlordFinder";
import AdminSellerPredictor from "../components/admin/intelligence/AdminSellerPredictor";
import AdminBuyerIntelligence from "../components/admin/intelligence/AdminBuyerIntelligence";
import AdminCopilot from "../components/admin/intelligence/AdminCopilot";
import AdminValuationEngine from "../components/admin/intelligence/AdminValuationEngine";
import AdminGrowthIntel from "../components/admin/intelligence/AdminGrowthIntel";
import AdminOpsIntel from "../components/admin/intelligence/AdminOpsIntel";
import { useToast } from "../hooks/use-toast";

const STAGES = ["new", "contacted", "qualified", "won", "lost"];
const stageColor = {
  new: "bg-blue-100 text-blue-700", contacted: "bg-amber-100 text-amber-700",
  qualified: "bg-purple-100 text-purple-700", won: "bg-green-100 text-green-700",
  lost: "bg-gray-100 text-gray-600",
};

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const buildGroups = (isFullAdmin) => [
  { key: "intelligence", label: "Intelligence", icon: Sparkles, tabs: [["command-centre", "Command Centre"], ["landlord-finder", "Landlord Finder"], ["seller-predictor", "Seller Predictor"], ["buyer-intel", "Buyer Intel"], ["copilot", "Copilot"], ["valuation-engine", "Valuation & Rent"], ["growth-intel", "Marketing & Revenue"], ["ops-intel", "Transactions & Mgmt"]] },
  { key: "pipeline", label: "Pipeline", icon: LayoutGrid, tabs: [["leads", "Leads / CRM"], ["calendar", "Calendar"], ["viewings", "Viewings"], ["enquiries", "Enquiries"], ["valuations", "Valuations"], ["matching", "Matching"], ["progression", "Progression"], ["analytics", "Analytics"], ["saved-searches", "Buyer Alerts"]] },
  { key: "listings", label: "Listings & Media", icon: Building2, tabs: [["properties", "Properties"], ["photos", "Photo Library"], ["blog", "Blog"]] },
  { key: "finance", label: "Finance", icon: PoundSterling, tabs: [["finance", "Finance"]] },
  { key: "operations", label: "Operations", icon: Wrench, tabs: [["compliance", "Compliance"], ["maintenance", "Maintenance"], ["payments", "Payments"], ["agency", "Agency"], ["templates", "Templates"], ["service-requests", "Landlord Services"]] },
  { key: "system", label: "System", icon: Settings, tabs: [["users", "Users"], ...(isFullAdmin ? [["emailtemplate", "Email Template"], ["suppressions", "Suppression List"], ["import", "Data Import"], ["appsync", "App Sync"], ["integrations", "API Keys"], ["apis", "All APIs"]] : [])] },
];

const Admin = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [leads, setLeads] = useState([]);
  const [viewings, setViewings] = useState([]);
  const [valuations, setValuations] = useState([]);
  const [enquiries, setEnquiries] = useState([]);

  const isAdmin = user && (user.role === "admin" || user.role === "agent");
  const GROUPS = buildGroups(user && user.role === "admin");
  const [group, setGroup] = useState("intelligence");
  const [active, setActive] = useState("command-centre");
  const [flat, setFlat] = useState(false);
  const currentGroup = GROUPS.find((g) => g.key === group) || GROUPS[0];

  const refresh = () => {
    getLeads().then(setLeads).catch(() => {});
    getViewings().then(setViewings).catch(() => {});
    getAdminValuations().then(setValuations).catch(() => {});
    getAdminEnquiries().then(setEnquiries).catch(() => {});
  };

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin]);

  const changeStage = async (id, stage) => {
    try {
      await updateLeadStage(id, stage);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, stage } : l)));
      toast({ title: "Lead updated", description: `Moved to ${stage}` });
    } catch {
      toast({ title: "Update failed" });
    }
  };

  const [leadOpen, setLeadOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "", message: "", type: "enquiry" });
  const [csvText, setCsvText] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [noteText, setNoteText] = useState("");

  const isOverdue = (l) => l.followUp && new Date(l.followUp) < new Date(new Date().toDateString()) && !["won", "lost"].includes(l.stage);

  const submitNote = async (id) => {
    if (!noteText.trim()) return;
    try { const updated = await addLeadNote(id, noteText.trim()); setLeads((ls) => ls.map((l) => l.id === id ? updated : l)); setNoteText(""); toast({ title: "Note added" }); }
    catch { toast({ title: "Couldn't add note" }); }
  };
  const saveFollowup = async (id, date) => {
    try { const updated = await setLeadFollowup(id, date || null); setLeads((ls) => ls.map((l) => l.id === id ? updated : l)); toast({ title: date ? "Follow-up set" : "Follow-up cleared" }); }
    catch { toast({ title: "Couldn't set follow-up" }); }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    try { await addLeadManual(leadForm); toast({ title: "Lead added" }); setLeadOpen(false); setLeadForm({ name: "", email: "", phone: "", message: "", type: "enquiry" }); refresh(); }
    catch { toast({ title: "Couldn't add lead" }); }
  };

  const submitImport = async (e) => {
    e.preventDefault();
    // parse CSV: name,email,phone (header optional)
    const rows = csvText.split("\n").map((r) => r.trim()).filter(Boolean);
    const leadsArr = [];
    for (const row of rows) {
      const cols = row.split(",").map((c) => c.trim());
      if (!cols[0] || cols[0].toLowerCase() === "name") continue;
      if (!cols[1] || !cols[1].includes("@")) continue;
      leadsArr.push({ name: cols[0], email: cols[1], phone: cols[2] || "", type: "enquiry" });
    }
    if (leadsArr.length === 0) { toast({ title: "No valid rows", description: "Use format: name,email,phone" }); return; }
    try { const res = await importLeads(leadsArr); toast({ title: "Import complete", description: `${res.created} added, ${res.skipped} skipped` }); setImportOpen(false); setCsvText(""); refresh(); }
    catch { toast({ title: "Import failed" }); }
  };

  const handleConvert = async (lead) => {
    try {
      const res = await convertLead(lead.id, "user");
      toast({ title: "Lead converted", description: `Account created for ${res.email} (temp pw: ${res.tempPassword})` });
      refresh();
    } catch (err) { toast({ title: "Convert failed", description: err?.response?.data?.detail || "" }); }
  };

  const setViewingStatus = async (id, status) => {
    try { await updateViewingStatus(id, status); setViewings((vs) => vs.map((v) => v.id === id ? { ...v, status } : v)); toast({ title: `Viewing ${status}` }); }
    catch { toast({ title: "Update failed" }); }
  };
  const giveFeedback = async (id, interest) => {
    try { const u = await submitViewingFeedback(id, { interest }); setViewings((vs) => vs.map((v) => v.id === id ? u : v)); toast({ title: `Feedback saved — ${interest} interest` }); }
    catch { toast({ title: "Couldn't save feedback" }); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#8a8a8a] font-display text-[20px]">Loading...</div>;

  if (!isAdmin) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-5 text-center">
        <h1 className="font-display text-[30px] text-[#2b2b2b] mb-3">Admin access only</h1>
        <p className="text-[14px] text-[#8a8a8a]">Sign in with your agent or admin account to continue.</p>
      </div>
    );
  }

  const stats = [
    { label: "Total Leads", value: leads.length, icon: Users },
    { label: "Viewings", value: viewings.length, icon: CalendarCheck },
    { label: "Valuations", value: valuations.length, icon: TrendingUp },
    { label: "Enquiries", value: enquiries.length, icon: Mail },
  ];

  return (
    <div className="min-h-[70vh] bg-[#faf7f2]">
      <div className="bg-[#1c1c1e] py-14">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <p className="text-[12px] tracking-[0.28em] uppercase text-[#d9b98a] mb-2">Anadear CRM</p>
          <h1 className="font-display text-white text-[34px] md:text-[42px] font-300">Admin Dashboard</h1>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-3 sm:px-6 md:px-8 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {stats.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-6 border border-[#f0ece5]">
              <div className="flex items-center justify-between mb-3">
                <span className="w-11 h-11 rounded-xl bg-[#faf7f2] flex items-center justify-center"><s.icon size={20} className="text-[#b8935f]" /></span>
              </div>
              <p className="font-display text-[34px] font-500 text-[#2b2b2b] leading-none">{s.value}</p>
              <p className="text-[13px] text-[#8a8a8a] mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs value={active} onValueChange={setActive}>
          <div className="mb-6">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="flex flex-wrap gap-2" data-testid="admin-group-nav">
                {!flat && GROUPS.map((g) => (
                  <button key={g.key} onClick={() => { setGroup(g.key); setActive(g.tabs[0][0]); }} data-testid={`admin-group-${g.key}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-500 transition-colors ${group === g.key ? "bg-[#1c1c1e] text-white" : "bg-white border border-[#f0ece5] text-[#6a6a6a] hover:border-[#b8935f]"}`}>
                    <g.icon size={15} /> {g.label}
                  </button>
                ))}
              </div>
              <button onClick={() => setFlat((v) => !v)} data-testid="admin-flat-toggle" className="text-[12px] text-[#8a8a8a] hover:text-[#b8935f] underline underline-offset-2">{flat ? "Group tabs" : "Show all tabs"}</button>
            </div>
            <div className="flex flex-wrap gap-1.5 bg-white border border-[#f0ece5] rounded-xl p-1.5" data-testid="admin-subtabs">
              {(flat ? GROUPS.flatMap((g) => g.tabs) : currentGroup.tabs).map(([val, label]) => (
                <button key={val} onClick={() => setActive(val)} data-testid={`admin-tab-${val}`}
                  className={`px-4 py-2 rounded-lg text-[13px] transition-colors ${active === val ? "bg-[#b8935f] text-white" : "text-[#6a6a6a] hover:bg-[#faf7f2]"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <TabsContent value="leads"><AdminCRM /></TabsContent>
          <TabsContent value="command-centre"><AdminCommandCentre /></TabsContent>
          <TabsContent value="landlord-finder"><AdminLandlordFinder /></TabsContent>
          <TabsContent value="seller-predictor"><AdminSellerPredictor /></TabsContent>
          <TabsContent value="buyer-intel"><AdminBuyerIntelligence /></TabsContent>
          <TabsContent value="copilot"><AdminCopilot /></TabsContent>
          <TabsContent value="valuation-engine"><AdminValuationEngine /></TabsContent>
          <TabsContent value="growth-intel"><AdminGrowthIntel /></TabsContent>
          <TabsContent value="ops-intel"><AdminOpsIntel /></TabsContent>
          <TabsContent value="calendar"><AdminCalendar /></TabsContent>
          <TabsContent value="properties"><AdminProperties /></TabsContent>
          <TabsContent value="photos"><AdminPhotoLibrary /></TabsContent>
          <TabsContent value="blog"><AdminBlog /></TabsContent>
          <TabsContent value="finance"><AdminFinance /></TabsContent>
          {user.role === "admin" && <TabsContent value="import"><AdminDataImport /></TabsContent>}
          {user.role === "admin" && <TabsContent value="emailtemplate"><AdminEmailTemplate /></TabsContent>}
          {user.role === "admin" && <TabsContent value="suppressions"><AdminSuppressionList /></TabsContent>}
          {user.role === "admin" && <TabsContent value="appsync"><AdminAppSync /></TabsContent>}
          <TabsContent value="analytics"><AdminAnalytics /></TabsContent>
          <TabsContent value="saved-searches"><AdminSavedSearches /></TabsContent>
          <TabsContent value="matching"><AdminMatching /></TabsContent>
          <TabsContent value="progression"><AdminProgression /></TabsContent>
          <TabsContent value="compliance"><AdminCompliance /></TabsContent>
          <TabsContent value="maintenance"><AdminMaintenance /></TabsContent>
          <TabsContent value="payments"><AdminPayments /></TabsContent>
          <TabsContent value="agency"><AdminAgency /></TabsContent>
          <TabsContent value="templates"><AdminTemplates /></TabsContent>
          <TabsContent value="service-requests"><AdminServiceRequests /></TabsContent>
          {user.role === "admin" && <TabsContent value="integrations"><AdminIntegrations /></TabsContent>}
          {user.role === "admin" && <TabsContent value="apis"><AdminApiDirectory /></TabsContent>}
          <TabsContent value="users"><AdminUsers /></TabsContent>
          <TabsContent value="viewings"><AdminViewings /></TabsContent>
          <TabsContent value="valuations"><AdminValuations /></TabsContent>
          <TabsContent value="enquiries"><AdminEnquiries /></TabsContent>
        </Tabs>

        <Dialog open={leadOpen} onOpenChange={setLeadOpen}>
          <DialogContent className="sm:max-w-[440px]">
            <h2 className="font-display text-[24px] font-300 text-[#2b2b2b] mb-4">Add Lead</h2>
            <form onSubmit={submitLead} className="space-y-3">
              <input required placeholder="Full name" value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} className="w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
              <input required type="email" placeholder="Email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} className="w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
              <input placeholder="Phone (optional)" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} className="w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
              <textarea placeholder="Notes / message" value={leadForm.message} onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] resize-none" />
              <button type="submit" className="w-full h-[48px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors">Add Lead</button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className="sm:max-w-[480px]">
            <h2 className="font-display text-[24px] font-300 text-[#2b2b2b] mb-2">Import Leads (CSV)</h2>
            <p className="text-[13px] text-[#8a8a8a] mb-4">Paste rows in the format <span className="text-[#b8935f]">name,email,phone</span> — one per line.</p>
            <form onSubmit={submitImport} className="space-y-3">
              <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={6} placeholder={"Jane Doe,jane@email.com,07700 900000\nJohn Smith,john@email.com,07700 900111"} className="w-full px-3 py-2 rounded-lg border border-[#e2d8c8] text-[13px] font-mono outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] resize-none" />
              <button type="submit" className="w-full h-[48px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors">Import</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Admin;
